import { animateOnce } from "../utilities/animation.js";
import { createElement, createFragment } from "../utilities/elements.js";
import { defineElement } from "./registry.js";
import { DickTokStandaloneComponent } from "./StandaloneComponent.js";

/**
 * UI component for a button with styles and touch animation.
 *
 * #### Toggle button
 * Can act as a toggle button by adding the `toggle` attribute. If the
 * `"selected"` slot has content, this will be shown when the button is active.
 * For example:
 * ```html
 *  <dt-button toggle>
 *    <span>Follow</span>
 *    <span slot="selected">Unfollow</span>
 *  </dt-button>
 * ```
 *
 * #### Adding icons
 * Icon can be added to the button using the `icon-name` attribute. The icon
 * can be customized with the `icon-style` (`"regular"`, `"solid"`) and
 * `icon-position` (`"start"`, `"end"`) attributes.
 *
 * #### Icon button
 * If the element has an icon and no children, an icon button style is applied.
 * This can be set manually using the `icon-button` attribute. This enables
 * using an icon for a toggle button by passing an icon to the slots.
 */
export class DickTokButton extends DickTokStandaloneComponent {
  static attributes = [
    { name: "icon-button", type: Boolean },
    { name: "icon-name", type: String, optional: true },
    { name: "icon-position", type: String, optional: true },
    { name: "icon-style", type: String, optional: true },
    { name: "selected", type: Boolean },
    { name: "toggle", type: Boolean },
  ];

  static targets = [
    "button",
    "slot",
    "content",
    "selectedSlot",
    "selectedContent",
  ];

  static template = createFragment`
    <button
        class="dt-button__button"
        data-target="button"
        data-action="click:dt-button->pressed"
    >
        <div class="dt-button__content" data-target="content">
            <slot data-target="slot"></slot>
        </div>
        <div class="dt-button__selected-content" data-target="selectedContent">
            <slot name="selected" data-target="selectedSlot"></slot>
        </div>
    </button>`;

  connectedCallback() {
    super.connectedCallback();
    this.#appendIcon();
  }

  #appendIcon() {
    if (this.hasIconName) {
      const style = this.iconStyle ?? "solid";
      const icon = createElement(
        `<i class="fa-sharp fa-${style} fa-${this.iconName}"></i>`,
      );
      this.iconPosition === "after"
        ? this.buttonTarget.append(icon)
        : this.buttonTarget.prepend(icon);
    }
    if (
      (this.hasIconName && !this.hasNodes(this.slotTarget)) ||
      this.iconButton
    ) {
      this.buttonTarget.classList.add("dt-button__button--icon");
    }
  }

  pressed() {
    if (this.toggle) {
      this.selected = !this.selected;
    }
    animateOnce(this.buttonTarget, "dt-button__button--pressed");
  }
}

defineElement("dt-button", DickTokButton);
